import { skipToken, useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import * as analytics from '@gitbook/app-analytics/client';
import { SearchModal, type SearchModalRef } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { shortcuts, useKeyboardShortcuts } from '../../Keyboard';
import {
    useDeferredSearchQuery,
    useNavigationState,
    useSetAnalyticsScreen,
} from '../../navigation';
import { apiQuery } from '../../state';
import { useAISearchQuery } from '../AISearch';
import { AppSearchLensFooter } from './AppSearchLensFooter';
import { AppSearchBody } from './Search';

/**
 * Global component to show the search modal when opened.
 */
export function AppSearch() {
    const [query, { open, close, setQuery }, askAI] = useDeferredSearchQuery();

    useKeyboardShortcuts(
        {
            [shortcuts.SEARCH_OPEN]: () => {
                open();
            },
        },
        ['application', 'editor-selection-collapsed']
    );

    if (typeof query === 'string') {
        return (
            <React.Suspense fallback={null}>
                <AppSearchModal query={query} setQuery={setQuery} close={close} askAI={askAI} />
            </React.Suspense>
        );
    }
    return null;
}

function AppSearchModal(props: {
    query: string;
    setQuery: (query: string) => void;
    close: () => void;
    askAI: boolean;
}): React.ReactElement {
    const { query, setQuery, close, askAI } = props;

    const searchRef = React.useRef<SearchModalRef>(null);
    const [aiQuery, setAIQuery] = useAISearchQuery();
    const [usingAI, setUsingAI] = React.useState(askAI);

    // Lookup current organization
    const { screen, overlay } = useNavigationState();
    const currentScreen = overlay || screen;

    const organizationId = 'organization' in currentScreen ? currentScreen.organization : undefined;

    const { data: organization } = useSuspenseQuery(
        apiQuery('getOrganizationById', organizationId ? { organizationId } : skipToken)
    );

    const aiEnabled = organization?.ai;

    /**
     * User is submitting a query to the AI.
     */
    const onSubmitAIQuery = useEventCallback(
        (newQuery: string, trigger?: 'click_ask_gitbook' | 'press_enter' | 'query_param') => {
            if (!aiEnabled || !organization) {
                return;
            }

            setUsingAI(true);
            setQuery(newQuery);
            setAIQuery(newQuery);
            searchRef.current?.focus();

            analytics.logEvent({
                type: 'ai_search_submit',
                organization: organization.id,
                query: newQuery,
                trigger: trigger ?? 'press_enter',
            });
        }
    );

    /**
     * User is clicking somewhere that updates the query.
     */
    const onUpdateQuery = (newQuery: string) => {
        setQuery(newQuery);
        searchRef.current?.focus();
    };

    /**
     * User is changing the query, so we update the Query and also revert back to search-view.
     */
    const onQueryChange = (query) => {
        setUsingAI(false);
        setQuery(query);
    };

    React.useEffect(() => {
        if (askAI) {
            if (query && query !== aiQuery.query) {
                onSubmitAIQuery(query, 'query_param');
            }
        }
    }, [query, askAI, aiQuery.query, onSubmitAIQuery]);

    /*
     * We use searchType to differentiate whether an ai search has been triggered by deep linking with the ask_ai=true query param
     */
    useSetAnalyticsScreen(
        {
            screen: 'search',
            searchType: askAI ? 'ai' : 'search',
            organization: organizationId,
        },
        [askAI, organizationId]
    );

    return (
        <SearchModal
            ref={searchRef}
            inputIcon={aiEnabled ? Icon.SearchOrAsk : Icon.Search}
            query={query}
            onQueryChange={onQueryChange}
            onSubmitQuery={onSubmitAIQuery}
            onClose={close}
            queryPlaceholder={aiEnabled ? 'Ask or search...' : 'Search content...'}
            footer={organization ? <AppSearchLensFooter organization={organization} /> : null}
        >
            <AppSearchBody
                query={query}
                aiQuery={aiQuery}
                usingAI={usingAI}
                organization={organization}
                onUpdateQuery={onUpdateQuery}
                onSubmitAIQuery={onSubmitAIQuery}
            />
        </SearchModal>
    );
}
